'use client';

import React, { useCallback } from 'react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { DefaultListingResultSize } from '@/utils/listing-filters';
import { PageOutput } from '@/utils/platform-api/__generated__/graphql';
import clsx from 'clsx';

export interface PagerProps {
  page: PageOutput;
  noResultsText?: string;
  /** When provided, pagination uses this instead of Next.js Link navigation. */
  onNavigate?: (url: string) => void;
  /**
   * Source for the current query string when building pager links. Required
   * alongside `onNavigate` so links reflect filters applied via
   * `history.pushState` (which `useSearchParams()` does not observe).
   */
  searchParams?: URLSearchParams;
}

const PagerLink: React.FC<{
  href: string;
  onNavigate?: (url: string) => void;
  className?: string;
  'aria-label': string;
  'aria-disabled'?: boolean;
  children: React.ReactNode;
}> = ({ href, onNavigate, children, ...rest }) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!onNavigate) return;
      e.preventDefault();
      onNavigate(href);
    },
    [href, onNavigate]
  );

  if (onNavigate) {
    return (
      <a href={href} onClick={handleClick} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <Link href={href} scroll {...rest}>
      {children}
    </Link>
  );
};

export const Pager: React.FC<PagerProps> = ({
  page,
  noResultsText,
  onNavigate,
  searchParams: searchParamsProp,
}) => {
  const hookSearchParams = useSearchParams();
  const searchParams = searchParamsProp ?? hookSearchParams;
  const nextParams = new URLSearchParams(searchParams);
  const offset = page.offset || 0;
  const size = searchParams.has('size')
    ? parseInt(searchParams.get('size') || DefaultListingResultSize.toString())
    : DefaultListingResultSize;
  const total = page.total || 0;
  nextParams.set('offset', `${Math.min(offset + size, total)}`);
  nextParams.set('size', `${size}`);
  const prevParams = new URLSearchParams(searchParams);
  prevParams.set('offset', `${Math.max(offset - size, 0)}`);
  prevParams.set('size', `${size}`);
  const pathname = usePathname();
  const disableNext = offset + size >= total;
  const disablePrev = offset === 0;
  const totalPages = Math.ceil(total / size);

  if (noResultsText && total === 0) {
    return (
      <span className="ut-xs p-[1rem] text-center text-gray-8">
        {noResultsText}
      </span>
    );
  }
  if (totalPages <= 1 || total === 0) {
    return null;
  }
  const currentPage = Math.floor(offset / size) + 1;
  const visiblePages = getVisiblePages(currentPage, totalPages);

  return (
    <nav className="flex w-fit items-center justify-center gap-[1rem] p-[1rem]">
      <PagerLink
        aria-label="Previous page"
        href={`${pathname}?${prevParams.toString()}`}
        onNavigate={onNavigate}
        className={clsx(
          disablePrev && 'pointer-events-none opacity-50',
          'text-gray-8 hover:text-gray-9 lg:p-0 p-2 transition-colors border lg:border-0 border-gray-8 hover:border-gray-9 rounded-full'
        )}
        aria-disabled={disablePrev}
      >
        <svg
          className="rotate-180 h-6 w-6 lg:h-4 lg:w-4 -translate-x-0.5 lg:translate-x-0"
          viewBox="0 0 14 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="currentColor"
            d="M13.2103 13.2103C13.8798 12.5409 13.8798 11.4538 13.2103 10.7843L2.92807 0.502063C2.25865 -0.167356 1.17151 -0.167356 0.502095 0.502063C-0.167323 1.17148 -0.167323 2.25862 0.502095 2.92804L9.57406 12L0.507448 21.072C-0.16197 21.7414 -0.16197 22.8285 0.507448 23.4979C1.17687 24.1674 2.264 24.1674 2.93342 23.4979L13.2157 13.2157L13.2103 13.2103Z"
          />
        </svg>
      </PagerLink>
      <div className="invisible w-full flex-row items-center justify-center gap-[0.5rem] md:visible flex">
        {visiblePages.map((item, index) => {
          if (item.type === 'ellipsis') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="text-gray-8 text-xl"
              >
                ...
              </span>
            );
          }

          const page = item.page;
          const currentOffset = page * size - size;
          const selected = currentOffset === offset;
          const navParams = new URLSearchParams(searchParams);
          navParams.set('offset', `${currentOffset}`);
          navParams.set('size', `${size}`);

          return (
            <PagerLink
              aria-label={`Go to page ${page}`}
              key={`page-${page}-link`}
              href={`${pathname}?${navParams.toString()}`}
              onNavigate={onNavigate}
              className={clsx(
                'flex p-4 w-8 h-8 items-center justify-center rounded-full text-center text-xl font-normal transition-colors hover:no-underline',
                selected
                  ? 'bg-secondary-main text-secondary-contrastText'
                  : 'text-gray-8 hover:bg-secondary-light hover:text-secondary-contrastText'
              )}
            >
              {page}
            </PagerLink>
          );
        })}
      </div>
      <PagerLink
        aria-label="Next page"
        href={`${pathname}?${nextParams.toString()}`}
        onNavigate={onNavigate}
        className={clsx(
          disableNext && 'pointer-events-none opacity-50',
          'text-gray-8 hover:text-gray-9 relative lg:p-0 p-2 transition-colors border lg:border-0 border-gray-8 hover:border-gray-9 rounded-full'
        )}
        aria-disabled={disableNext}
      >
        <svg
          className='h-6 w-6 lg:h-4 lg:w-4 translate-x-0.5 lg:translate-x-0'
          viewBox="0 0 14 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="currentColor"
            d="M13.2103 13.2103C13.8798 12.5409 13.8798 11.4538 13.2103 10.7843L2.92807 0.502063C2.25865 -0.167356 1.17151 -0.167356 0.502095 0.502063C-0.167323 1.17148 -0.167323 2.25862 0.502095 2.92804L9.57406 12L0.507448 21.072C-0.16197 21.7414 -0.16197 22.8285 0.507448 23.4979C1.17687 24.1674 2.264 24.1674 2.93342 23.4979L13.2157 13.2157L13.2103 13.2103Z"
          />
        </svg>
      </PagerLink>
    </nav>
  );
};

export default Pager;

function getVisiblePages(
  currentPage: number,
  totalPages: number
): Array<{ type: 'page'; page: number } | { type: 'ellipsis' }> {
  const result: Array<{ type: 'page'; page: number } | { type: 'ellipsis' }> = [];

  // Always show first page
  result.push({ type: 'page', page: 1 });

  if (totalPages <= 7) {
    // Show all pages if 7 or fewer
    for (let i = 2; i <= totalPages; i++) {
      result.push({ type: 'page', page: i });
    }
    return result;
  }

  // Calculate which pages to show
  let startPage = Math.max(2, currentPage - 1);
  let endPage = Math.min(totalPages - 1, currentPage + 1);

  // Adjust if we're near the start
  if (currentPage <= 3) {
    startPage = 2;
    endPage = Math.min(4, totalPages - 1);
  }

  // Adjust if we're near the end
  if (currentPage >= totalPages - 2) {
    startPage = Math.max(2, totalPages - 3);
    endPage = totalPages - 1;
  }

  // Add ellipsis and pages
  if (startPage > 2) {
    result.push({ type: 'ellipsis' });
  }

  for (let i = startPage; i <= endPage; i++) {
    result.push({ type: 'page', page: i });
  }

  if (endPage < totalPages - 1) {
    result.push({ type: 'ellipsis' });
  }

  // Always show last page
  result.push({ type: 'page', page: totalPages });

  return result;
}
